import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CreateProjectInput, Project, ProjectStatus } from '@catalyst-psa/shared';
import { PROJECT_STATUSES } from '@catalyst-psa/shared';

import { ApiError, mutationError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { CheckboxField, SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { CustomerSelectField } from '../customers/CustomerSelectField.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import { useBillingClassOptions } from '../billing-classes/api.js';
import { useAgreements } from '../agreements/api.js';
import {
  useCreateProject,
  useDeleteProject,
  useMigrateProjectHoursToAgreement,
  useProjectManagers,
  useUpdateProject,
} from './api.js';
import { PROJECT_STATUS_LABELS } from './status.js';

interface ProjectFormModalProps {
  isOpen: boolean;
  /** Existing project to edit, or null to create a new one. */
  project: Project | null;
  /** When creating, pre-selects this customer (e.g. launched from an onboarding record). */
  defaultCustomerId?: string;
  /** When creating, pre-fills the project name. */
  defaultName?: string;
  /** When creating, pre-selects the default service category. */
  defaultServiceCategoryId?: string;
  /** When creating, links the new project to this source opportunity. */
  defaultOpportunityId?: string;
  /** When creating, pre-selects the project manager (e.g. the PMO's pre-assigned PM). */
  defaultProjectManagerId?: string;
  onClose: () => void;
  /** Called with the created project after a successful create. */
  onCreated?: (project: Project) => void;
}

interface FormState {
  customerId: string;
  name: string;
  status: ProjectStatus;
  description: string;
  isBillable: boolean;
  isChargeCodeProject: boolean;
  projectManagerId: string;
  defaultServiceCategoryId: string;
  defaultBillingClassId: string;
  startDate: string;
  endDate: string;
  internalNotes: string;
  managedServicesAgreementId: string;
}

const EMPTY_FORM: FormState = {
  customerId: '',
  name: '',
  status: 'active',
  description: '',
  isBillable: true,
  isChargeCodeProject: false,
  projectManagerId: '',
  defaultServiceCategoryId: '',
  defaultBillingClassId: '',
  startDate: '',
  endDate: '',
  internalNotes: '',
  managedServicesAgreementId: '',
};

function toFormState(
  project: Project | null,
  defaults?: {
    customerId?: string;
    name?: string;
    serviceCategoryId?: string;
    projectManagerId?: string;
  },
): FormState {
  if (!project) {
    return {
      ...EMPTY_FORM,
      customerId: defaults?.customerId ?? '',
      name: defaults?.name ?? '',
      defaultServiceCategoryId: defaults?.serviceCategoryId ?? '',
      projectManagerId: defaults?.projectManagerId ?? '',
    };
  }
  return {
    customerId: project.customerId,
    name: project.name,
    status: project.status,
    description: project.description ?? '',
    isBillable: project.isBillable,
    isChargeCodeProject: project.isChargeCodeProject,
    projectManagerId: project.projectManagerId,
    defaultServiceCategoryId: project.defaultServiceCategoryId,
    defaultBillingClassId: project.defaultBillingClassId,
    startDate: project.startDate ?? '',
    endDate: project.endDate ?? '',
    internalNotes: project.internalNotes ?? '',
    managedServicesAgreementId: project.managedServicesAgreementId ?? '',
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

const STATUS_OPTIONS = PROJECT_STATUSES.map((status) => ({
  value: status,
  label: PROJECT_STATUS_LABELS[status],
}));

export function ProjectFormModal({
  isOpen,
  project: projectProp,
  defaultCustomerId,
  defaultName,
  defaultServiceCategoryId,
  defaultOpportunityId,
  defaultProjectManagerId,
  onClose,
  onCreated,
}: ProjectFormModalProps): JSX.Element {
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(projectProp);
  const isEdit = project !== null;
  const { isAdmin } = useAuth();
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(() =>
    toFormState(project, {
      customerId: defaultCustomerId,
      name: defaultName,
      serviceCategoryId: defaultServiceCategoryId,
      projectManagerId: defaultProjectManagerId,
    }),
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [migrateConfirmOpen, setMigrateConfirmOpen] = useState(false);

  const createMutation = useCreateProject();
  const updateMutation = useUpdateProject();
  const deleteMutation = useDeleteProject();
  const migrateMutation = useMigrateProjectHoursToAgreement();
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetMigrate = migrateMutation.reset;
  const resetSaveMode = saveMode.reset;

  const serviceCategoriesQuery = useServiceCategoryOptions();
  const serviceCategoryOptions = useMemo(
    () =>
      (serviceCategoriesQuery.data ?? []).map((category) => ({
        value: category.id,
        label: category.name,
      })),
    [serviceCategoriesQuery.data],
  );

  const billingClassesQuery = useBillingClassOptions();
  const billingClassOptions = useMemo(
    () =>
      (billingClassesQuery.data ?? []).map((billingClass) => ({
        value: billingClass.id,
        label: billingClass.name,
      })),
    [billingClassesQuery.data],
  );

  const managersQuery = useProjectManagers();
  const managerOptions = useMemo(
    () =>
      (managersQuery.data ?? []).map((member) => ({
        value: member.id,
        label: member.displayName,
      })),
    [managersQuery.data],
  );

  // Managed services agreements on this project's customer, for the "bill via MS agreement"
  // link. Only same-customer managed_services agreements are eligible (one per customer).
  const msAgreementsQuery = useAgreements(
    { page: 1, pageSize: 200, customerId: form.customerId, type: 'managed_services' },
    { enabled: Boolean(form.customerId) },
  );
  const msAgreementOptions = useMemo(
    () => [
      { value: '', label: 'Bill as project time (default)' },
      ...(msAgreementsQuery.data?.data ?? []).map((agreement) => ({
        value: agreement.id,
        label: agreement.name,
      })),
    ],
    [msAgreementsQuery.data],
  );

  useEffect(() => {
    if (isOpen) {
      setProject(projectProp);
    }
  }, [isOpen, projectProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(
        toFormState(project, {
          customerId: defaultCustomerId,
          name: defaultName,
          serviceCategoryId: defaultServiceCategoryId,
          projectManagerId: defaultProjectManagerId,
        }),
      );
      setConfirmOpen(false);
      setMigrateConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
      resetMigrate();
    }
  }, [
    isOpen,
    project,
    defaultCustomerId,
    defaultName,
    defaultServiceCategoryId,
    defaultProjectManagerId,
    resetSaveMode,
    resetCreate,
    resetUpdate,
    resetMigrate,
  ]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save project.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const payload: CreateProjectInput = {
      customerId: form.customerId,
      name: form.name.trim(),
      status: form.status,
      description: form.description.trim() ? form.description.trim() : null,
      isBillable: form.isBillable,
      isChargeCodeProject: form.isChargeCodeProject,
      projectManagerId: form.projectManagerId,
      defaultServiceCategoryId: form.defaultServiceCategoryId,
      defaultBillingClassId: form.defaultBillingClassId,
      startDate: form.startDate ? form.startDate : null,
      endDate: form.endDate ? form.endDate : null,
      internalNotes: form.internalNotes.trim() ? form.internalNotes.trim() : null,
      opportunityId: defaultOpportunityId ?? null,
      managedServicesAgreementId: form.managedServicesAgreementId
        ? form.managedServicesAgreementId
        : null,
    };

    if (isEdit) {
      const {
        name,
        status,
        description,
        isBillable,
        isChargeCodeProject,
        projectManagerId,
        defaultServiceCategoryId,
        defaultBillingClassId,
        startDate,
        endDate,
        internalNotes,
        managedServicesAgreementId,
      } = payload;
      updateMutation.mutate(
        {
          id: project.id,
          input: {
            name,
            status,
            description,
            isBillable,
            isChargeCodeProject,
            projectManagerId,
            defaultServiceCategoryId,
            defaultBillingClassId,
            startDate,
            endDate,
            internalNotes,
            managedServicesAgreementId,
          },
        },
        {
          onSuccess: (updated) => {
            // Keep the saved snapshot current so the migrate-hours button sees the
            // just-saved agreement link when the modal stays open.
            setProject(updated);
            if (saveMode.closeAfterSaveRef.current) onClose();
          },
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          onCreated?.(created);
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setProject(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!project) {
      return;
    }
    deleteMutation.mutate(project.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  const goToCustomer = (): void => {
    const targetCustomerId = form.customerId || project?.customerId;
    if (!targetCustomerId) {
      return;
    }
    navigate(`/customers?returnCustomerId=${targetCustomerId}&returnTab=projects`);
  };

  // Migrate is only offered against the SAVED agreement link — an unsaved change in the
  // dropdown must be saved first so hours attach to the agreement actually stored.
  const savedMsAgreementId = project?.managedServicesAgreementId ?? null;
  const msLinkDirty = form.managedServicesAgreementId !== (savedMsAgreementId ?? '');
  const canMigrate = isEdit && Boolean(savedMsAgreementId) && !msLinkDirty;
  const migrateResult = migrateMutation.data ?? null;
  const migrateError = mutationError(
    migrateMutation.error,
    'Failed to migrate the un-billed hours.',
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit project' : 'New project'}
        size="wide"
        headerActions={
          <Button
            variant="secondary"
            size="sm"
            onClick={goToCustomer}
            disabled={!form.customerId && !project?.customerId}
          >
            Go to Customer
          </Button>
        }
        footer={
          <FormActions
            formId="project-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit && isAdmin ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form id="project-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          <CustomerSelectField
            label="Customer"
            required
            disabled={isEdit}
            activeOnly
            value={form.customerId}
            onChange={(customerId) => setForm((f) => ({ ...f, customerId }))}
            error={fieldErrors.customerId}
          />

          <div className="grid grid-cols-2 gap-4">
            <SelectField
              label="Project manager"
              required
              value={form.projectManagerId}
              placeholder={managersQuery.isLoading ? 'Loading…' : 'Select a manager'}
              options={managerOptions}
              onChange={(e) => setForm((f) => ({ ...f, projectManagerId: e.target.value }))}
              error={fieldErrors.projectManagerId}
            />
            <SelectField
              label="Status"
              value={form.status}
              options={STATUS_OPTIONS}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as ProjectStatus }))}
              error={fieldErrors.status}
            />
          </div>

          <TextField
            label="Name"
            required
            value={form.name}
            maxLength={200}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={fieldErrors.name}
          />

          <TextAreaField
            label="Description"
            rows={3}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            error={fieldErrors.description}
          />

          <div className="grid grid-cols-2 gap-4">
            <SelectField
              label="Default service category"
              required
              value={form.defaultServiceCategoryId}
              placeholder={serviceCategoriesQuery.isLoading ? 'Loading…' : 'Select a category'}
              options={serviceCategoryOptions}
              onChange={(e) => setForm((f) => ({ ...f, defaultServiceCategoryId: e.target.value }))}
              error={fieldErrors.defaultServiceCategoryId}
            />
            <SelectField
              label="Default billing class"
              required
              value={form.defaultBillingClassId}
              placeholder={billingClassesQuery.isLoading ? 'Loading…' : 'Select a billing class'}
              options={billingClassOptions}
              onChange={(e) => setForm((f) => ({ ...f, defaultBillingClassId: e.target.value }))}
              error={fieldErrors.defaultBillingClassId}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="Start date"
              type="date"
              value={form.startDate}
              onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
              error={fieldErrors.startDate}
            />
            <TextField
              label="End date"
              type="date"
              value={form.endDate}
              onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
              error={fieldErrors.endDate}
            />
          </div>

          <CheckboxField
            label="Billable"
            checked={form.isBillable}
            onChange={(e) => setForm((f) => ({ ...f, isBillable: e.target.checked }))}
          />

          <div>
            <div className="flex items-end gap-3">
              <div className="min-w-0 flex-1">
                <SelectField
                  label="Bill via managed services agreement"
                  value={form.managedServicesAgreementId}
                  options={msAgreementOptions}
                  disabled={!form.customerId}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, managedServicesAgreementId: e.target.value }))
                  }
                  error={fieldErrors.managedServicesAgreementId}
                />
              </div>
              {isEdit && savedMsAgreementId ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setMigrateConfirmOpen(true)}
                  disabled={!canMigrate || migrateMutation.isPending}
                  isLoading={migrateMutation.isPending}
                >
                  Migrate un-billed hours
                </Button>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-slate-500">
              When set, this project&apos;s time is consumed against the agreement&apos;s prepaid
              hours (shown on Cat+ Usage and the Managed Services invoice as included/overage)
              instead of billing as project time.
            </p>
            {isEdit && savedMsAgreementId && msLinkDirty ? (
              <p className="mt-1 text-xs text-amber-600">
                Save the project before migrating hours so they attach to the saved agreement.
              </p>
            ) : null}
            {migrateResult ? (
              <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                Attached {migrateResult.migrated} un-billed time{' '}
                {migrateResult.migrated === 1 ? 'entry' : 'entries'} to{' '}
                {migrateResult.agreementName}.
                {migrateResult.skippedInvoiced > 0
                  ? ` Skipped ${migrateResult.skippedInvoiced} already-invoiced ${
                      migrateResult.skippedInvoiced === 1 ? 'entry' : 'entries'
                    }.`
                  : ''}
                {migrateResult.alreadyAttached > 0
                  ? ` ${migrateResult.alreadyAttached} ${
                      migrateResult.alreadyAttached === 1 ? 'entry was' : 'entries were'
                    } already on the agreement.`
                  : ''}
              </div>
            ) : null}
            {migrateError ? (
              <div className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {migrateError}
              </div>
            ) : null}
          </div>

          <CheckboxField
            label="Charge code project (its tickets are used as time-entry charge codes)"
            checked={form.isChargeCodeProject}
            onChange={(e) => setForm((f) => ({ ...f, isChargeCodeProject: e.target.checked }))}
          />

          <TextAreaField
            label="Internal notes"
            rows={4}
            value={form.internalNotes}
            onChange={(e) => setForm((f) => ({ ...f, internalNotes: e.target.value }))}
            error={fieldErrors.internalNotes}
          />
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete project"
        message={`Delete "${project?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
      <ConfirmDialog
        isOpen={migrateConfirmOpen}
        title="Migrate un-billed hours"
        message={`Attach all of this project's un-billed hours to "${
          project?.managedServicesAgreementName ?? 'the linked agreement'
        }"? They will consume the agreement's prepaid hours (Cat+ Usage / Managed Services invoicing). Hours already on an invoice are skipped.`}
        confirmLabel="Migrate hours"
        isConfirming={migrateMutation.isPending}
        onConfirm={() => {
          if (!project) return;
          migrateMutation.mutate(project.id, {
            onSuccess: () => setMigrateConfirmOpen(false),
            onError: () => setMigrateConfirmOpen(false),
          });
        }}
        onCancel={() => setMigrateConfirmOpen(false)}
      />
    </>
  );
}
